// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { getCookie } from "@insite/client-framework/Common/Cookies";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCartLineContext, withCartLine } from "@insite/client-framework/Components/CartLineContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { isOutOfStock } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import removeCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/RemoveCartLine";
import updateCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/UpdateCartLine";
import translate from "@insite/client-framework/Translate";
import {
    AvailabilityMessageType,
    ProductSettingsModel,
    PromotionModel,
} from "@insite/client-framework/Types/ApiModels";
import { NavigationModeCookieName } from "@insite/content-library/Components/NavigationModeMenu";
import ProductBinNumbers from "@insite/content-library/Components/ProductBinNumbers";
import { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import CartLineQuantity, { CartLineQuantityStyles } from "@insite/content-library/Widgets/Cart/CartLineQuantity";
import Clickable from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconMemo, IconPresentationProps } from "@insite/mobius/Icon";
import XCircle from "@insite/mobius/Icons/XCircle";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import Tooltip, { TooltipPresentationProps } from "@pace/MobiusOverrides/Tooltip";
import ProductAvailability, { ProductAvailabilityStyles } from "@pace/Overrides/Components/ProductAvailability";
import { ProductPartNumbersStyles } from "@pace/Overrides/Components/ProductPartNumbers";
import { UnitOfMeasure } from "@pace/Overrides/Components/ProductPrice";
import IconAlert from "@pace/XDL/Icons/IconAlert";
import React, { FC } from "react";
import { connect, HandleThunkActionCreator } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    cart: Cart;
    promotions: PromotionModel[];
    productSettings: ProductSettingsModel;
    showInventoryAvailability?: boolean;
    updateCartLine: HandleThunkActionCreator<typeof updateCartLine>;
    removeCartLine: HandleThunkActionCreator<typeof removeCartLine>;
    showRemoveAction?: boolean;
    extendedStyles?: CartLineCardCondensedStyles;
    showVmiBinDetails?: boolean;
}

// XNG-Changes
const mapStateToProps = (state: ApplicationState) => ({
    enableVat: getSettingsCollection(state).productSettings.enableVat,
    vatPriceDisplay: getSettingsCollection(state).productSettings.vatPriceDisplay,
    showPricing: state.context.showPricing,
});

type Props = OwnProps & HasCartLineContext & ReturnType<typeof mapStateToProps>;

export interface CartLineCardCondensedStyles {
    container?: GridContainerProps;
    productImageGridItem?: GridItemProps;
    productImage?: ProductImageStyles;
    cartLineInfoGridItem?: GridItemProps;
    cartLineInfoContainer?: GridContainerProps;
    productBrandAndDescriptionGridItem?: GridItemProps;
    productBrand?: ProductBrandStyles;
    productDescription?: ProductDescriptionStyles;
    productErpNumberText?: TypographyProps;
    productPriceAndQuantityGridItem?: GridItemProps;
    productPriceAndQuantityContainer?: GridContainerProps;
    productPriceAndAvailabilityGridItem?: GridItemProps;
    productPrice?: ProductPriceStyles;
    productAvailability?: ProductAvailabilityStyles;
    cartLineErrorMessageText?: TypographyProps;
    quantityGridItem?: GridItemProps;
    quantity?: CartLineQuantityStyles;
    removeCartLineGridItem?: GridItemProps;
    removeCartLineIcon?: IconPresentationProps;
    productBinNumberGridItem?: GridItemProps;
    productBinNumbers?: ProductPartNumbersStyles;
    // XNG-Changes
    mainContainer?: InjectableCss;
    productDescriptionContainer?: InjectableCss;
    productErpNumberContainer?: InjectableCss;
    productErpNumberLabel?: TypographyProps;
    productAvailabilityContainer?: InjectableCss;
    quantityContainer?: InjectableCss;
    productPriceContainer?: InjectableCss;
    productPriceText?: TypographyProps;
    removeCartLineContainer?: InjectableCss;
    specialOrderIcon?: IconPresentationProps;
    specialOrderTooltip?: TooltipPresentationProps;
}

export const cartLineCardCondensedStyles: CartLineCardCondensedStyles = {
    container: {
        gap: 20,
        css: css`
            border-bottom: 1px solid ${getColor("common.border")};
            padding: 1rem 0;
        `,
    },
    productImageGridItem: {
        width: 2,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-size: 10px;
                    `,
                    css`
                        font-size: 10px;
                    `,
                    css`
                        font-size: 10px;
                    `,
                    css`
                        font-size: 10px;
                    `,
                    null,
                ])}
        `,
    },
    cartLineInfoGridItem: { width: [8, 8, 8, 9, 9] },
    productBrandAndDescriptionGridItem: {
        width: [12, 12, 12, 6, 5],
        css: css`
            flex-direction: column;
        `,
    },
    productErpNumberText: {
        css: css`
            width: 100%;
            word-wrap: break-word;
        `,
    },
    productPriceAndQuantityGridItem: { width: [12, 12, 12, 6, 7] },
    productPriceAndAvailabilityGridItem: {
        width: 6,
        css: css`
            flex-direction: column;
        `,
    },
    cartLineErrorMessageText: { color: "danger" },
    quantityGridItem: { width: 6 },
    removeCartLineGridItem: {
        css: css`
            justify-content: center;
        `,
        width: [2, 2, 2, 1, 1],
    },
    removeCartLineIcon: { src: XCircle },
    productBinNumberGridItem: {
        width: [12, 12, 12, 3, 3],
        css: css`
            flex-direction: column;
        `,
    },
};

const CartLineCardCondensed: FC<Props> = ({
    cart,
    cartLine,
    showInventoryAvailability,
    removeCartLine,
    updateCartLine,
    extendedStyles,
    showRemoveAction,
    showVmiBinDetails,
    // XNG-Changes
    enableVat,
    vatPriceDisplay,
    showPricing,
}) => {
    const currentMode = getCookie(NavigationModeCookieName);
    const qtyOrderedChangeHandler = (qtyOrdered: number) => {
        if (qtyOrdered !== cartLine.qtyOrdered) {
            if (qtyOrdered <= 0) {
                removeCartLine({ cartLineId: cartLine.id });
            } else {
                updateCartLine({
                    cartLine: {
                        ...cartLine,
                        qtyOrdered,
                    },
                });
            }
        }
    };

    const removeCartLineClickHandler = () => {
        if (!showRemoveAction) {
            return;
        }

        removeCartLine({ cartLineId: cartLine.id });
    };

    const sumQtyPerUom = cart.cartLines!.reduce((sum, current) => {
        return current.productId === cartLine.productId
            ? sum + current.qtyPerBaseUnitOfMeasure * current.qtyOrdered!
            : sum;
    }, 0);
    const errorMessages: React.ReactNode[] = [];
    if (showInventoryAvailability && cartLine.hasInsufficientInventory && !isOutOfStock(cartLine)) {
        const tooManyRequestedMessage = siteMessage(
            "Cart_ToManyQtyRequested",
            cartLine.qtyOnHand.toLocaleString(),
            sumQtyPerUom.toLocaleString(),
        );
        errorMessages.push(tooManyRequestedMessage);
    }

    if (cartLine.isRestricted) {
        errorMessages.push(translate("Restricted product"));
    }

    if (!cartLine.isActive) {
        errorMessages.push(translate("Inactive Product"));
    }

    if (cartLine.cantBuy) {
        errorMessages.push(translate("View-only product"));
    }

    const [styles] = React.useState(() => mergeToNew(cartLineCardCondensedStyles, extendedStyles));

    return (
        <StyledWrapper
            {...styles.mainContainer}
            data-test-selector={`cartline_condensed_${cartLine.productId}_${cartLine.unitOfMeasure}`}
        >
            <StyledWrapper {...styles.productDescriptionContainer}>
                {/* XNG-Changes:remove brand */}
                {/* {cartLine.brand && <ProductBrand brand={cartLine.brand} extendedStyles={styles.productBrand} />} */}
                <ProductDescription product={cartLine} extendedStyles={styles.productDescription} />
            </StyledWrapper>
            <StyledWrapper {...styles.productErpNumberContainer}>
                <Typography {...styles.productErpNumberLabel}>{translate("Part:")}</Typography>
                <Typography {...styles.productErpNumberText}>{cartLine.erpNumber}</Typography>
            </StyledWrapper>
            {showVmiBinDetails && currentMode === "Vmi" && (
                <StyledWrapper {...styles.productBinNumberGridItem}>
                    <ProductBinNumbers
                        locationId={cart.vmiLocationId}
                        binId={cartLine.vmiBinId}
                        isCondensed={true}
                        extendedStyles={styles.productBinNumbers}
                    />
                </StyledWrapper>
            )}
            <StyledWrapper {...styles.productAvailabilityContainer}>
                {showInventoryAvailability && !cartLine.quoteRequired && (
                    <ProductAvailability
                        productId={cartLine.productId!}
                        availability={cartLine.availability!}
                        unitOfMeasure={cartLine.unitOfMeasure}
                        trackInventory={false}
                        availabilityResult={{
                            qtyInCustomerBranch: cartLine.properties["qtyInCustomerBranch"],
                            qtyInDc: cartLine.properties["qtyInDc"],
                            qtyInNetwork: cartLine.properties["qtyInNetwork"],
                        }}
                        failedToLoadInventory={cart.failedToGetRealTimeInventory}
                        extendedStyles={styles.productAvailability}
                    />
                )}
            </StyledWrapper>
            <StyledWrapper {...styles.quantityContainer}>
                <CartLineQuantity
                    cart={cart}
                    editable={true}
                    onQtyOrderedChange={qtyOrderedChangeHandler}
                    extendedStyles={styles.quantity}
                />
            </StyledWrapper>
            <StyledWrapper {...styles.productPriceContainer}>
                <StyledWrapper {...styles.productPriceAndAvailabilityGridItem}>
                    {!cartLine.quoteRequired && !cart.cartNotPriced && cartLine.pricing && showPricing && (
                        <StyledWrapper>
                            <Typography {...styles.productPriceText}>
                                {enableVat && vatPriceDisplay !== "DisplayWithoutVat"
                                    ? cartLine.pricing.extendedUnitRegularPriceWithVatDisplay
                                    : cartLine.pricing.extendedUnitNetPriceDisplay}
                                {/*  XNG-Changes: add special Order icon */}
                                {cartLine.availability?.messageType === AvailabilityMessageType.OutOfStock && (
                                    <Tooltip
                                        text={siteMessage("Custom_Cart_Total_Special_Order")}
                                        triggerComponent={<Icon {...styles.specialOrderIcon} src={IconAlert} />}
                                        {...styles.specialOrderTooltip}
                                    />
                                )}
                            </Typography>
                        </StyledWrapper>
                    )}
                    {/** XNG-Changes: PAC-580 */}
                    {cartLine.quoteRequired && (
                        <StyledWrapper>
                            <Typography {...styles.productPriceText} data-test-selector="quoteRequiredText">
                                {translate("Requires Quote")}
                            </Typography>
                            <Tooltip text={siteMessage("Rfq_TooltipMessage").toString()} />
                            <UnitOfMeasure
                                unitOfMeasure={cartLine?.unitOfMeasureDescription || cartLine?.unitOfMeasureDisplay}
                                showUnitOfMeasure={true}
                            />
                        </StyledWrapper>
                    )}
                    {/** XNG-Changes: PAC-693 */}
                    {cart.cartNotPriced && (
                        <StyledWrapper>
                            <Typography
                                {...styles.productPrice?.price?.errorText}
                                data-test-selector="cartLinPrice_PricingMessage"
                            >
                                {siteMessage("RealTimePricing_PriceLoadFailed")}
                            </Typography>
                        </StyledWrapper>
                    )}
                    {/* XNG-Changes: remove error */}
                    {/*  {errorMessages.length > 0 &&
                        errorMessages.map((message, index) => (
                            <Typography
                                {...styles.cartLineErrorMessageText}
                                // eslint-disable-next-line react/no-array-index-key
                                key={index}
                                data-test-selector="cartline_errorMessage"
                            >
                                {message}
                            </Typography>
                        ))} */}
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper {...styles.removeCartLineContainer}>
                {showRemoveAction && (
                    <Clickable
                        onClick={removeCartLineClickHandler}
                        data-test-selector="cartline_removeLine"
                        aria-label="Remove item"
                    >
                        <VisuallyHidden>{translate("Remove item")}</VisuallyHidden>
                        <IconMemo {...styles.removeCartLineIcon} />
                    </Clickable>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps)(withCartLine(CartLineCardCondensed));
